import { generatePath, getCurrentURL, matchPath } from '@Alias/utils';
import Uri from 'jsuri';
import { capitalize } from 'lodash';
import { SECURE_BASE_URL } from '../config';
import { DynamicPath, Path } from '../constants/router';
import { SignupStep } from '../types/billing';
import { Primitive } from '../types/common';
import { LaunchType, PlayerAsset } from '../types/launch';
import { purgePersistedReduxState } from './persist-store';

export enum AssetType {
  BROADCAST = 'broadcast',
  VOD = 'vod',
}

const SUB_PATHS: string[] = [Path.GO, Path.LOGIN, Path.PLAYER];
const PATH_REGEX = new RegExp(`/((${SUB_PATHS.join('|')})/)*(\\w+)`, 'i');

export const BASE = `/${Path.LOGIN}`;

export const LOGIN = BASE;

export const SUBSCRIBE_NESTED = `${BASE}/${Path.SUBSCRIBE}`;

export const SIGNUP_NESTED = `${BASE}/${Path.SIGNUP}`;

export const AUTHENTICATE_NESTED = `${BASE}/${Path.AUTHENTICATE}`;

export const AUTHORIZE_NESTED = `${BASE}/${Path.AUTHORIZE}`;

export const GEO_FENCE_NESTED = `${BASE}/${Path.GEO_FENCE}`;

export const PLAYER = `/${Path.PLAYER}`;
export const MY_TV = `${PLAYER}/mytv`;

export const ACCOUNT = '/account';
export const ACCOUNT_NESTED = `${SECURE_BASE_URL}${ACCOUNT}`;

export const LAUNCH_CODE = 'launchCode';
export const LAUNCH = `/${Path.GO}`;

const ID = 'id';
const SHOW = `${PLAYER}/${LaunchType.SHOW}/:${ID}`;

export const PATTERN_PLAYABLE_ASSET = `${PLAYER}/${Path.PLAYER}/:type/:typeId`;

export const LINK = `${PLAYER}/${Path.LINK}`;

export const REDEEM_PAGE_TITLE = 'Redeem';

const HOST = /(\.|^)(philo|tivli)\.com$/i;
const SCHEME = /^https?$/i;

export const ROUTE_TITLES: Record<string, string> = Object.values(Path).reduce(
  (acc, path: string) => (SUB_PATHS.includes(path) ? acc : { ...acc, [path]: capitalize(path) }),
  {
    [Path.LOGIN]: capitalize(Path.LOGIN),
  },
);

export const SignupPageTitle: Record<number, string> = {
  [SignupStep.PLAN]: 'selectPaymentPlan',
  [SignupStep.PAYMENT]: 'payment',
};

export function safeRedirectURL(redirectURL: string) {
  const uri = typeof redirectURL === 'string' ? new Uri(redirectURL) : redirectURL;
  const host = uri.host();
  if (host !== '' && !HOST.test(host)) {
    return false;
  }
  const protocol = uri.protocol();
  if (protocol !== '' && !SCHEME.test(protocol)) {
    return false;
  }
  return true;
}

export function isGoPath(path?: string | null) {
  return (
    !!path && [DynamicPath.CANONICAL, DynamicPath.GO].some(pattern => matchPath(pattern, path))
  );
}

export function isActivatePath(path?: string | null) {
  return !!(path && matchPath(DynamicPath.ACTIVATE, path));
}

export function isShowPath(path: string) {
  return !!matchPath(SHOW, path);
}

export function isSignupPath(path: string) {
  return !!matchPath(SIGNUP_NESTED, path);
}

export function isAuthenticatePath(path: string) {
  return [Path.AUTHENTICATE, AUTHENTICATE_NESTED].some(pattern => matchPath(pattern, path));
}

export function isAuthorizePath(path: string) {
  return [Path.AUTHORIZE, AUTHORIZE_NESTED].some(pattern => matchPath(pattern, path));
}

export function isValidAppPath(
  path: string | null | undefined,
  appPaths = [
    SUBSCRIBE_NESTED,
    AUTHENTICATE_NESTED,
    AUTHORIZE_NESTED,
    GEO_FENCE_NESTED,
    SIGNUP_NESTED,
  ],
) {
  return (
    appPaths.some(pattern => matchPath(pattern, String(path))) ||
    isGoPath(path) ||
    isActivatePath(path)
  );
}

export function isValidFooterPath(path: string) {
  return !isSignupPath(path) && isValidAppPath(path);
}

export function createAppPathAndPersistQuery(
  url: string,
  additionalParams: Record<string, Primitive> = {},
) {
  const newURL = new Uri(url);

  const currentParams = getCurrentURL().searchParams;
  // KU – iterate over an array to avoid this issue with Samsung Internet browsers
  // https://sentry.io/organizations/philo/issues/3497821266
  [...currentParams.entries(), ...Object.entries(additionalParams)].forEach(([param, value]) =>
    newURL.replaceQueryParam(param, value),
  );

  return newURL.toString();
}

export function createRedirectPath(redirectPath: string) {
  const currentPath = new Uri(getCurrentURL().toString());
  const newPath = new Uri(redirectPath);

  const debug = currentPath.getQueryParamValue('debug');
  currentPath.deleteQueryParam('debug');
  newPath.addQueryParam('redirect_uri', currentPath.toString());
  if (debug) {
    newPath.addQueryParam('debug', debug);
  }

  return newPath.toString();
}

export function createGoURL(launchCode: string) {
  return generatePath(DynamicPath.GO, { [LAUNCH_CODE]: launchCode });
}

export function createActivateURL(code: string) {
  return generatePath(DynamicPath.ACTIVATE, { activationCode: code });
}

export function getPathToPlayerAsset({ assetId, assetType }: PlayerAsset) {
  return `${PLAYER}/${assetType}/${assetId}`;
}

export function createShowURL(showId: string) {
  return getPathToPlayerAsset({ assetId: showId, assetType: LaunchType.SHOW });
}

export function createChannelURL(channelId: string) {
  return getPathToPlayerAsset({ assetId: channelId, assetType: LaunchType.CHANNEL });
}

export function getPageTitle(pathname: string) {
  const route = PATH_REGEX.exec(pathname)?.pop() ?? '';
  return ROUTE_TITLES[route];
}

export function redirectToExternalURL(url: string) {
  // JD: If we are redirecting out of the login app,
  // login is complete and we should purge the login app's state.
  purgePersistedReduxState();
  // DR: Give Segment some time to send events since they don't provide
  // a way to know that events were sent.
  window.setTimeout(() => {
    window.location.href = url;
  }, 250);
}
